import type { ISODateString, Loan, LoanEvent } from '../../types';
import {
  computeSchedule,
  countRemainingGenuineCycles,
  getContractualMaturity,
  getNextScheduledPayment,
  type ComputedSchedule,
  type ContractualMaturity,
  type ProjectionAssumption,
  type ProjectionInfo,
  type RegularEmiBasis,
  type ScheduleMonth,
} from './engine';
import { formatDate, formatInr } from '../format';

/**
 * ============================================================================
 * LOAN STATE — one shared view over the single calculation engine
 * ============================================================================
 *
 * Home, Schedule and the PDF need the same derived facts (position,
 * projection, next payment, maturity vs. modelled payoff, the basis of the
 * estimates). They are assembled ONCE here from computeSchedule(); nothing in
 * this module calculates money itself.
 *
 * Projections are always available once a disbursement exists. Where LIC HFL
 * has not confirmed how payments are allocated, the engine uses an explicit,
 * editable working assumption (see resolveRepaymentArrangement) and the
 * figures are flagged as estimates.
 */

export interface LoanStateSummary {
  asOfDate: ISODateString;
  /** The loan as of `asOfDate` (calculated schedule up to that date). */
  position: ComputedSchedule;
  /** The unrestricted projection (to closure). */
  projection: ComputedSchedule;
  phase: 'pre_emi' | 'regular' | 'closed';
  /** Principal per ACTUAL recorded events only; scheduled repayments never reduce it. */
  recordedPrincipal: number;
  /** Sum of recorded disbursements effective on/before asOfDate. */
  totalDisbursed: number;
  /** approvedAmount minus ALL recorded disbursements (never negative). NOT outstanding principal. */
  undisbursedAmount: number;
  /** Calculated next payment after asOfDate (interest-only during pre-EMI). Never lender-confirmed. */
  nextPayment?: ScheduleMonth;
  /** Regular EMI that applies / will apply, with its provenance. */
  regularEmi?: { amount: number; basis: RegularEmiBasis };
  /** The baseline EMI for the full sanctioned amount. Never replaced by any other figure. */
  originalEmi: number;
  lenderReported?: Loan['lenderReported'];
  /** Contractual maturity: lender-reported when entered, else derived from tenure (labelled). */
  contractualMaturity?: ContractualMaturity;
  projectionInfo: ProjectionInfo;
  /** Modelled payoff date (a calculated estimate; distinct from contractual maturity). */
  modelledClosureDate?: ISODateString;
  /** Modelled remaining regular instalments. */
  remainingRegularInstalments?: number;
  /** True when scheduled (calculated) principal repayments have reduced the modelled balance below the recorded principal. */
  modelledBalanceDiffersFromRecorded: boolean;
  /** True when any headline figure rests on an assumption rather than a recorded/lender figure. */
  isEstimate: boolean;
}

export interface SummarizeLoanStateOptions {
  loan: Loan;
  events: LoanEvent[];
  asOfDate: ISODateString;
}

export function summarizeLoanState({ loan, events, asOfDate }: SummarizeLoanStateOptions): LoanStateSummary {
  const projection = computeSchedule({ loan, events });
  const position = computeSchedule({ loan, events, asOfDate });

  const totalDisbursedEver = events
    .filter((e): e is Extract<LoanEvent, { type: 'disbursement' }> => !e.supersededAt && e.type === 'disbursement')
    .reduce((sum, e) => sum + e.amount, 0);

  const projectionInfo = projection.summary.projection;
  const isModelled = projectionInfo.status === 'modelled';
  const recordedPrincipal = position.summary.currentRecordedPrincipal;

  return {
    asOfDate,
    position,
    projection,
    phase: position.summary.currentPhase,
    recordedPrincipal,
    totalDisbursed: position.summary.totalDisbursed,
    undisbursedAmount: Math.max(0, loan.approvedAmount - totalDisbursedEver),
    nextPayment: position.summary.isLoanClosed ? undefined : getNextScheduledPayment(projection, asOfDate),
    regularEmi:
      position.summary.currentApplicableEmi > 0 && position.summary.regularEmiBasis
        ? { amount: position.summary.currentApplicableEmi, basis: position.summary.regularEmiBasis }
        : undefined,
    originalEmi: loan.originalEmiAtFullDisbursement,
    lenderReported: loan.lenderReported,
    contractualMaturity: getContractualMaturity(loan, events),
    projectionInfo,
    modelledClosureDate: isModelled ? projection.summary.projectedClosureDate : undefined,
    remainingRegularInstalments: isModelled
      ? position.summary.isLoanClosed
        ? 0
        : countRemainingGenuineCycles(projection, position)
      : undefined,
    modelledBalanceDiffersFromRecorded:
      Math.abs(position.summary.currentOutstandingPrincipal - recordedPrincipal) > 0.5 && !position.summary.isLoanClosed,
    isEstimate: isModelled,
  };
}

// ---------------------------------------------------------------------------
// Concise wording for the estimate basis (shared by Home, Schedule and the PDF)
// ---------------------------------------------------------------------------

export interface TextFormatters {
  inr: (amount: number, precise?: boolean) => string;
  date: (iso: string | undefined) => string;
}

const DEFAULT_FORMATTERS: TextFormatters = { inr: formatInr, date: formatDate };

const DAY_COUNT_TEXT: Record<Loan['dayCountConvention'], string> = {
  actual_365_fixed: 'Actual/365',
  actual_365_25: 'Actual/365.25',
  actual_360: 'Actual/360',
};

/** One short phrase per assumption. */
export function describeAssumption(a: ProjectionAssumption, fmt: TextFormatters = DEFAULT_FORMATTERS): string {
  switch (a.code) {
    case 'arrangement': {
      if (!a.firstRegularDueDate) return 'regular EMI from the first EMI date';
      const origin = a.startDateSource === 'automatic' ? 'working assumption, editable' : a.source === 'user_selected' ? 'your chosen assumption' : 'your saved Regular EMI Start Date';
      return `payments before ${fmt.date(a.firstRegularDueDate)} are interest-only, regular EMI from then (${origin})`;
    }
    case 'undisbursed_never_disbursed':
      return `${fmt.inr(a.amount)} undisbursed stays undisbursed`;
    case 'emi_recorded':
      return 'regular EMI as recorded';
    case 'emi_original_full_disbursement':
      return 'original EMI applies once fully disbursed';
    case 'emi_derived_over_contract_tenure':
      return `regular EMI of ${fmt.inr(a.emi, true)} re-derived on the disbursed principal over ${a.instalments} instalments to ${fmt.date(a.maturityDate)}`;
    case 'rate_unchanged':
      return `rate stays ${a.ratePercent.toFixed(2)}%`;
    case 'no_further_prepayments':
      return 'no further prepayments';
    case 'payments_on_due_dates':
      return 'payments made in full on due dates';
    case 'day_count':
      return `${DAY_COUNT_TEXT[a.convention]} day count`;
  }
}

/** A single discreet line, e.g. for a tooltip or small caption under projections. */
export function describeEstimateBasis(info: ProjectionInfo, fmt: TextFormatters = DEFAULT_FORMATTERS): string | undefined {
  if (info.status !== 'modelled') return undefined;
  const text = info.assumptions.map((a) => describeAssumption(a, fmt)).join('; ');
  return `Estimate: ${text}.`;
}

export const REGULAR_EMI_BASIS_LABEL: Record<RegularEmiBasis, string> = {
  recorded_event: 'Recorded',
  recorded_loan: 'Recorded',
  original_full_disbursement: 'Original EMI',
  derived_over_contract_tenure: 'Est.',
};

// ---------------------------------------------------------------------------
// Loan status — separate from repayment behaviour
// ---------------------------------------------------------------------------

export type DisbursementState = 'none' | 'partial' | 'full';

/** How much of the approved amount has actually been disbursed (recorded events only). */
export function getDisbursementState(loan: Loan, events: LoanEvent[]): DisbursementState {
  const total = events
    .filter((e): e is Extract<LoanEvent, { type: 'disbursement' }> => !e.supersededAt && e.type === 'disbursement')
    .reduce((sum, e) => sum + e.amount, 0);
  if (total <= 0) return 'none';
  return total >= loan.approvedAmount - 0.005 ? 'full' : 'partial';
}

/**
 * Display status. A loan is shown as "Draft / Expected" only when it is a
 * draft record AND nothing shows it has actually been sanctioned (no
 * recorded disbursement, no lender-reported figures). Once it is sanctioned
 * the status describes disbursement ("Sanctioned · Partially disbursed"),
 * independent of how repayments are modelled. Stored data is never changed.
 */
export function getLoanStatusLabel(loan: Loan, events: LoanEvent[]): { label: string; isDraft: boolean } {
  const disbursement = getDisbursementState(loan, events);
  const looksSanctioned = disbursement !== 'none' || loan.lenderReported !== undefined;
  if (loan.status === 'draft_expected' && !looksSanctioned) return { label: 'Draft / Expected', isDraft: true };
  if (disbursement === 'partial') return { label: 'Sanctioned · Partially disbursed', isDraft: false };
  if (disbursement === 'full') return { label: 'Sanctioned · Fully disbursed', isDraft: false };
  return { label: loan.status === 'lic_confirmed' ? 'LIC Confirmed' : 'Sanctioned', isDraft: false };
}
